import {
	LIVE_CONNECT_PROVIDERS,
	MATRIX_TO_PLATFORM,
	type PlatformConnection,
} from "./connections-api.js";
import type { AgentId } from "./nav.js";
import { CONNECTORS, COMMS_CHANNELS } from "./nav.js";

export type KitConnector = {
	id: string;
	markId: string;
	name: string;
	required: boolean;
	/** connected when OAuth + bound account; missing until then; soon if catalog. */
	state: "connected" | "missing" | "soon";
};

export type KitTool = {
	id: string;
	label: string;
	tier: "tier-0" | "tier-1" | "tier-2" | "tier-3";
};

export type AgentKit = {
	agentId: AgentId;
	/** Short job description for the info modal. */
	job: string;
	connectors: KitConnector[];
	tools: KitTool[];
};

/**
 * Live kit state. Healthy/degraded OAuth is enough for every live connector —
 * portfolio rows are the inventory; a single focus bind is optional.
 */
export function kitConnectorState(
	providers: readonly PlatformConnection[] | undefined,
	matrixId: string,
	catalogSoon: boolean,
): KitConnector["state"] {
	if (catalogSoon) return "soon";
	if (!LIVE_CONNECT_PROVIDERS.has(matrixId)) return "missing";
	if (!providers) return "missing";
	const platformId = MATRIX_TO_PLATFORM[matrixId];
	if (!platformId) return "missing";
	const row = providers.find((p) => p.id === platformId);
	const conn = row?.connection;
	if (!conn || conn.health === "revoked") return "missing";
	if (conn.health !== "healthy" && conn.health !== "degraded") {
		return "missing";
	}
	return "connected";
}

function connector(
	id: string,
	required: boolean,
	providers: readonly PlatformConnection[] | undefined,
): KitConnector {
	const fromList =
		CONNECTORS.find((c) => c.id === id) ??
		COMMS_CHANNELS.find((c) => c.id === id);
	if (!fromList) {
		return {
			id,
			markId: id,
			name: id,
			required,
			state: kitConnectorState(providers, id, false),
		};
	}
	return {
		id: fromList.id,
		markId: fromList.markId,
		name: fromList.name,
		required,
		state: kitConnectorState(
			providers,
			fromList.id,
			fromList.status === "soon",
		),
	};
}

/**
 * Agent kit for the info modal — job brief, connectors, headline tools.
 * Pass live `providers` from GET /api/connections when available.
 */
export function kitFor(
	agentId: AgentId,
	providers?: readonly PlatformConnection[],
): AgentKit {
	const c = (id: string, required: boolean) =>
		connector(id, required, providers);

	switch (agentId) {
		case "onboarding":
			return {
				agentId,
				job: "Turns a website into a company profile — brand voice, basics, and what to connect next — before any ad spend.",
				connectors: [c("ga4", false), c("gsc", false), c("google_ads", false)],
				tools: [
					{ id: "research.crawl", label: "Site research", tier: "tier-0" },
					{
						id: "research.company_pack",
						label: "Company pack",
						tier: "tier-0",
					},
					{
						id: "tenant.config",
						label: "Workspace setup",
						tier: "tier-1",
					},
				],
			};
		case "paid_media":
			return {
				agentId,
				job: "Owns search paid media: briefs, paused campaign builds, and spend changes. Nothing goes live or spends without your approval.",
				connectors: [
					c("google_ads", true),
					c("linkedin_ads", false),
					c("ga4", false),
					c("gsc", false),
					c("google_drive", false),
				],
				tools: [
					{
						id: "google_ads.create_paused_search_stack",
						label: "Paused search stack",
						tier: "tier-2",
					},
					{
						id: "google_ads.raise_budget",
						label: "Raise budget",
						tier: "tier-2",
					},
					{
						id: "google_ads.set_campaign_status",
						label: "Campaign status",
						tier: "tier-2",
					},
					{
						id: "google_ads.create_keywords",
						label: "Keywords",
						tier: "tier-1",
					},
					{
						id: "google_ads.create_responsive_search_ad",
						label: "Responsive search ad",
						tier: "tier-1",
					},
					{
						id: "google_ads.create_campaign",
						label: "Create campaign",
						tier: "tier-1",
					},
				],
			};
		case "ga4_anomaly":
			return {
				agentId,
				job: "Watches every enabled GA4 property in the portfolio for unusual traffic swings. Read-only — GA4 is the connector; this agent never spends or publishes.",
				connectors: [c("ga4", true)],
				tools: [
					{ id: "ga4.run_report", label: "Run report", tier: "tier-0" },
				],
			};
		case "seo":
			return {
				agentId,
				job: "Organic search specialist — technical health, query opportunities, and page priorities. Publishes nothing without your approval.",
				connectors: [c("gsc", true), c("ga4", false)],
				tools: [
					{ id: "gsc.query_report", label: "Query report", tier: "tier-0" },
					{ id: "seo.audit", label: "Site audit", tier: "tier-0" },
					{ id: "seo.brief", label: "Page brief", tier: "tier-1" },
				],
			};
		case "content":
			return {
				agentId,
				job: "Drafts and briefs in your brand voice. You review drafts; publish waits on your approval.",
				connectors: [
					c("wordpress", false),
					c("webflow", false),
					c("notion", false),
					c("canva", false),
					c("google_drive", false),
				],
				tools: [
					{ id: "content.outline", label: "Outline", tier: "tier-0" },
					{ id: "content.draft", label: "Draft", tier: "tier-1" },
					{ id: "content.publish", label: "Publish", tier: "tier-2" },
				],
			};
		case "social":
			return {
				agentId,
				job: "Social calendar and channel-native drafts. Posts wait for your approval — nothing publishes on its own.",
				connectors: [
					c("instagram", false),
					c("linkedin", false),
					c("x", false),
					c("pinterest", false),
					c("meta_ads", false),
				],
				tools: [
					{ id: "social.calendar", label: "Calendar", tier: "tier-0" },
					{ id: "social.draft", label: "Draft post", tier: "tier-1" },
					{ id: "social.publish", label: "Publish", tier: "tier-2" },
				],
			};
		case "email":
			return {
				agentId,
				job: "Lifecycle and campaign email — sequences staged for approval before any send.",
				connectors: [
					c("klaviyo", false),
					c("mailchimp", false),
					c("hubspot", false),
				],
				tools: [
					{ id: "email.draft", label: "Draft campaign", tier: "tier-1" },
					{ id: "email.sequence", label: "Sequence", tier: "tier-1" },
					{ id: "email.send", label: "Send", tier: "tier-2" },
				],
			};
		case "creative":
			return {
				agentId,
				job: "Creative briefs and ad variants. Assets are proposed for review — nothing ships without your approval.",
				connectors: [
					c("google_drive", true),
					c("google_photos", false),
					c("canva", false),
					c("figma", false),
					c("dropbox", false),
					c("google_ads", false),
					c("meta_ads", false),
				],
				tools: [
					{ id: "drive.list_files", label: "List Drive files", tier: "tier-0" },
					{ id: "photos.list_media", label: "List Photos", tier: "tier-0" },
					{ id: "canva.list_designs", label: "List Canva designs", tier: "tier-0" },
					{ id: "figma.list_files", label: "List Figma files", tier: "tier-0" },
					{ id: "dropbox.list_folder", label: "List Dropbox folder", tier: "tier-0" },
					{ id: "creative.brief", label: "Creative brief", tier: "tier-0" },
					{ id: "creative.variants", label: "Variants", tier: "tier-1" },
					{ id: "creative.upload", label: "Upload asset", tier: "tier-1" },
				],
			};
		case "cro":
			return {
				agentId,
				job: "Conversion experiments — hypotheses, copy tests, and landing lifts. Changes wait on approval.",
				connectors: [c("ga4", true), c("google_ads", false)],
				tools: [
					{ id: "cro.hypotheses", label: "Hypotheses", tier: "tier-0" },
					{ id: "cro.experiment", label: "Experiment plan", tier: "tier-1" },
					{ id: "cro.ship_variant", label: "Ship variant", tier: "tier-2" },
				],
			};
		case "local":
			return {
				agentId,
				job: "Local presence — Google Business Profile, reviews, and nearby search demand.",
				connectors: [c("gbp", true), c("gsc", false)],
				tools: [
					{ id: "local.listing_audit", label: "Listing audit", tier: "tier-0" },
					{ id: "local.review_digest", label: "Review digest", tier: "tier-0" },
					{ id: "local.update_post", label: "GBP post", tier: "tier-1" },
				],
			};
		case "developer":
			return {
				agentId,
				job: "Sites, hosting, DNS, deploys, and WordPress. Checks readiness first; live CMS edits and production changes wait on your approval.",
				connectors: [
					c("github", true),
					c("vercel", true),
					c("cloudflare", true),
					c("godaddy", true),
					c("wordpress", true),
				],
				tools: [
					{ id: "developer.sites_readiness", label: "Sites readiness", tier: "tier-0" },
					{ id: "github.get_file_contents", label: "Read file", tier: "tier-0" },
					{ id: "github.create_pull_request", label: "Open draft PR", tier: "tier-1" },
					{ id: "github.merge_pull_request", label: "Merge PR", tier: "tier-2" },
					{ id: "vercel.list_deployments", label: "Deployments", tier: "tier-0" },
					{
						id: "vercel.trigger_preview_deployment",
						label: "Preview redeploy",
						tier: "tier-1",
					},
					{
						id: "vercel.promote_production",
						label: "Promote production",
						tier: "tier-2",
					},
					{ id: "cloudflare.list_dns_records", label: "DNS records", tier: "tier-0" },
					{ id: "cloudflare.draft_dns_change", label: "Draft DNS change", tier: "tier-1" },
					{ id: "cloudflare.upsert_dns_record", label: "Apply DNS", tier: "tier-2" },
					{ id: "wordpress.list_posts", label: "List posts/pages", tier: "tier-0" },
					{ id: "wordpress.get_post", label: "Read post", tier: "tier-0" },
					{ id: "wordpress.create_draft", label: "Create draft", tier: "tier-1" },
					{ id: "wordpress.update_draft", label: "Update draft", tier: "tier-1" },
					{
						id: "wordpress.update_live_post",
						label: "Update live page",
						tier: "tier-2",
					},
					{ id: "wordpress.publish_post", label: "Publish", tier: "tier-2" },
					{ id: "wordpress.list_widgets", label: "List widgets", tier: "tier-0" },
					{
						id: "wordpress.update_widget",
						label: "Update widget",
						tier: "tier-2",
					},
					{ id: "wordpress.list_menus", label: "List menus", tier: "tier-0" },
					{ id: "wordpress.update_menu", label: "Update menu", tier: "tier-2" },
					{
						id: "wordpress.list_template_parts",
						label: "List template parts",
						tier: "tier-0",
					},
					{
						id: "wordpress.update_template_part",
						label: "Update template part",
						tier: "tier-2",
					},
				],
			};
		case "reporting":
			return {
				agentId,
				job: "Cross-channel digests — what moved, why it matters, and what to do next. Recommendations wait on your approval.",
				connectors: [c("ga4", false), c("google_ads", false), c("gsc", false)],
				tools: [
					{ id: "report.digest", label: "Weekly digest", tier: "tier-0" },
					{ id: "report.narrative", label: "Narrative", tier: "tier-0" },
					{ id: "report.recommend", label: "Recommend action", tier: "tier-1" },
				],
			};
		default: {
			const _exhaustive: never = agentId;
			return _exhaustive;
		}
	}
}

export function tierLabel(tier: KitTool["tier"]): string {
	switch (tier) {
		case "tier-0":
			return "Read";
		case "tier-1":
			return "Review";
		case "tier-2":
			return "Approve";
		case "tier-3":
			return "Confirm";
		default: {
			const _exhaustive: never = tier;
			return _exhaustive;
		}
	}
}

export function kitStateLabel(state: KitConnector["state"]): string {
	switch (state) {
		case "connected":
			return "Connected";
		case "missing":
			return "Missing";
		case "soon":
			return "Soon";
		default: {
			const _exhaustive: never = state;
			return _exhaustive;
		}
	}
}
